import { buildUrlWithQuery } from '@/Utils/buildUrlWithQuery';
import { formatLastUpdated } from '@/Utils/formatLastUpdated';
import { $http } from '@/bootstrap.js';
import { eventBus, events } from '@/eventBus.js';
import type { ComputedRef, Ref } from 'vue';
import { computed, onBeforeUnmount, onMounted, onUnmounted, ref, watch } from 'vue';

interface ChartWidgetConfig {
    dataUrl: ComputedRef<string>;
    filters?: Ref<any>;
    parseResponse: (response: any) => {
        data: any;
        lastUpdated?: string;
        debug?: any;
    };
    fetchOnMount?: boolean;
}

interface ChartWidgetReturn {
    // State
    loading: Ref<boolean>;
    data: Ref<any>;
    lastUpdated: Ref<string>;
    componentKey: Ref<number>;
    hasData: ComputedRef<boolean>;
    debug: ComputedRef<boolean>;
    showDebugInfo: Ref<boolean>;
    debugInfo: Ref<any>;

    // Methods
    fetchData: (showLoading?: boolean) => Promise<void>;
}

export function useChartWidget(config: ChartWidgetConfig): ChartWidgetReturn {
    const { dataUrl, filters, parseResponse, fetchOnMount = true } = config;

    // State
    const loading = ref(false);
    const isFetching = ref(false);
    const data = ref<any>(null);
    const lastUpdated = ref('');
    const componentKey = ref(0);
    const showDebugInfo = ref(false);
    const debugInfo = ref<any>(null);

    // Computed
    const hasData = computed(() => {
        if (!data.value) {
            return false;
        }

        if (Array.isArray(data.value)) {
            return data.value.length > 0;
        }

        return Object.keys(data.value).length > 0;
    });

    const debug = computed(() => {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('debug') === '1';
    });

    // Methods
    const fetchData = async (showLoading = true) => {
        if (isFetching.value) return;

        isFetching.value = true;

        if (showLoading) {
            loading.value = true;
        }

        try {
            const params = {};

            if (filters?.value) {
                const filterData = filters.value?.form?.data?.();

                if (filterData) {
                    Object.assign(params, {
                        locations: filterData.locations,
                        specificLocations: filterData.specificLocations,
                        areas: filterData.areas,
                        types: filterData.types,
                        start: filterData.start,
                        end: filterData.end,
                        grouping: filterData.grouping,
                    });
                }
            }

            if (debug.value) {
                params.debug = 1;
            }

            const url = buildUrlWithQuery(dataUrl.value, params);
            const response = await $http.get(url);

            const parsed = parseResponse(response);
            data.value = parsed.data;

            if (parsed.lastUpdated) {
                lastUpdated.value = formatLastUpdated(parsed.lastUpdated);
            }

            if (parsed.debug) {
                debugInfo.value = parsed.debug;
            }

            componentKey.value++;
        } catch (error) {
            console.error('Error fetching chart data:', error);
        } finally {
            isFetching.value = false;
            loading.value = false;
        }
    };

    // Lifecycle
    const handleResize = () => {
        componentKey.value++;
    };

    const handleDashboardRefresh = () => {
        fetchData(false);
    };

    onMounted(() => {
        window.addEventListener('resize', handleResize);
        eventBus.$on(events.dashboardWidgetRefresh, handleDashboardRefresh);
    });

    onBeforeUnmount(() => {
        window.removeEventListener('resize', handleResize);
    });

    onUnmounted(() => {
        eventBus.$off(events.dashboardWidgetRefresh, handleDashboardRefresh);
    });

    // Watchers
    if (filters) {
        watch(
            filters,
            () => {
                fetchData();
            },
            { deep: true },
        );
    }

    return {
        // State
        loading,
        data,
        lastUpdated,
        componentKey,
        hasData,
        debug,
        showDebugInfo,
        debugInfo,

        // Methods
        fetchData,
    };
}
